"use client";

import React, { useEffect } from "react";
import { MapPin, Phone, Mail, ArrowRight, Loader, Send, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Toaster } from "@/components/ui/sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SHARED_DATA = {
  address: "124 Sherwood Drive Brantford",
  phone: "289 439 5348",
  email: "Sampson.info101@gmail.com",
  businessName: "Sampson Concrete & Masonry",
  location: "Springfield"
};
export default function ConnectWithUs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="3332" id="discuss-your-site-requirements" className="relative w-full bg-background text-foreground py-24 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(hsl(var(--accent))_1px,transparent_1px)] [background-size:24px_24px] opacity-5 pointer-events-none" />
      
      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {}
          <div className="flex flex-col space-y-10">
            <div className="space-y-6">
              
              
            </div>

            <div className="space-y-8">
              {[{
              icon: Mail,
              label: "Project Inquiries",
              value: SHARED_DATA.email,
              href: `mailto:${SHARED_DATA.email}`,
              action: "Send details"
            }, {
              icon: Phone,
              label: "Direct Line",
              value: SHARED_DATA.phone,
              href: `tel:${SHARED_DATA.phone.replace(/\D/g, '')}`,
              action: "Speak with an expert"
            }, {
              icon: MapPin,
              label: "Main Office",
              value: SHARED_DATA.address,
              href: "#",
              action: "View on map",
              isMap: true
            }].map((item, i) => <div key={i} data-index={i} className="group flex items-start gap-6 p-6 rounded-sm border border-transparent hover:border-border hover:bg-card/40 transition-all duration-300">
                  <div className="shrink-0 flex items-center justify-center size-12 rounded-sm bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <item.icon className="size-5 stroke-[2.5px]" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{item.label}</p>
                    <p className="text-lg font-bold text-foreground/90 font-sans">{item.value}</p>
                    
                    {item.isMap ? <Sheet>
                        <SheetTrigger asChild>
                          <Button variant="link" className="p-0 h-auto text-primary font-bold hover:no-underline group-hover:translate-x-1 transition-transform uppercase text-xs tracking-wider">
                            {item.action} <ArrowRight className="ml-1 size-4" />
                          </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-full sm:max-w-lg p-0 border-r border-border bg-card">
                          <SheetHeader className="p-6 border-b border-border bg-card/95 backdrop-blur-sm absolute top-0 left-0 right-0 z-10">
                            <SheetTitle className="font-black tracking-tighter uppercase">Our Location</SheetTitle>
                            <SheetDescription className="font-sans">
                              Visit our headquarters in {SHARED_DATA.location} for project consultations.
                            </SheetDescription>
                          </SheetHeader>
                          <div className="w-full h-full pt-[88px]">
                            <iframe width="100%" height="100%" style={{
                        border: 0
                      }} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" src={`https://www.google.com/maps/embed/v1/place?key=YOUR_API_KEY&q=${encodeURIComponent(SHARED_DATA.address)}`} className="w-full h-full bg-muted grayscale contrast-125" title="Office Location">
                              {}
                              <div className="flex items-center justify-center h-full bg-muted text-muted-foreground font-mono text-xs">
                                MAP PREVIEW (API KEY REQUIRED)
                              </div>
                            </iframe>
                          </div>
                        </SheetContent>
                      </Sheet> : <Link to={item.href} className="inline-flex items-center text-xs font-bold text-primary hover:text-primary/80 transition-colors mt-1 group-hover:translate-x-1 duration-300 uppercase tracking-wider">
                        {item.action} <ArrowRight className="ml-1 size-4" />
                      </Link>}
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/5 rounded-sm blur-3xl opacity-50 pointer-events-none" />
            <div className="relative bg-card border-t-2 border-t-primary border-x border-b border-border/50 rounded-sm p-8 md:p-10 shadow-2xl shadow-black/40">
              <div className="mb-8">
                <h3 className="text-2xl font-black text-foreground uppercase tracking-tighter">Project Inquiry</h3>
                <p className="text-muted-foreground mt-2 font-sans">
                  Outline your structural requirements below for a detailed project assessment and quote.
                </p>
              </div>

              <FormProvider formKey="site_requirements_form_contact" formId="site_requirements_form_contact" sectionName="discuss-your-site-requirements" defaultValues={{
              name: "",
              email: "",
              phone: "",
              reason: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 50
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true,
                minLength: 10,
                maxLength: 1000
              }
            }} submitText="Request Quote" successMessage="Thank you! Your project details have been received. A Sampson representative will contact you shortly." errorMessage="Transmission error. Please verify your details and try again.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
      <Toaster />
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("Inquiry Received", {
        description: "Our planning team will review your requirements.",
        duration: 5000
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-sm bg-primary/20 flex items-center justify-center text-primary mb-2">
          <Send className="size-8" />
        </div>
        <h4 className="text-xl font-black text-foreground uppercase tracking-tighter">Inquiry Sent</h4>
        <p className="text-muted-foreground max-w-xs mx-auto font-sans">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-sm border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold uppercase tracking-widest text-xs">
          New Project Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-background border-border rounded-sm focus-visible:ring-primary h-11" />
              </FormControl>
              <FormMessage className="text-[10px] font-mono uppercase" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-background border-border rounded-sm focus-visible:ring-primary h-11" />
              </FormControl>
              <FormMessage className="text-[10px] font-mono uppercase" />
            </FormItem>} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest">Phone Number <span className="text-muted-foreground font-normal lowercase ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="bg-background border-border rounded-sm focus-visible:ring-primary h-11" />
              </FormControl>
              <FormMessage className="text-[10px] font-mono uppercase" />
            </FormItem>} />

        <FormField control={control} name="reason" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest">Service Type <span className="text-muted-foreground font-normal lowercase ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-11 w-full items-center justify-between rounded-sm border border-border bg-background px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50 appearance-none font-sans" {...field} value={field.value || ""}>
                    <option value="" disabled>Select a service</option>
                    {["Concrete Pouring", "Masonry Work", "Structural Foundations", "Commercial Project"].map((opt, i) => <option key={i} value={opt} data-index={i}>{opt}</option>)}
                  </select>
                  <div className="absolute right-3 top-3.5 pointer-events-none opacity-50">
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage className="text-[10px] font-mono uppercase" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest">Project Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your site requirements and timeline..." className="min-h-[150px] bg-background border-border rounded-sm focus-visible:ring-primary resize-none font-sans" {...field} />
            </FormControl>
            <FormMessage className="text-[10px] font-mono uppercase" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-xs font-mono uppercase text-destructive flex items-center gap-2">
          <span className="font-black">Error:</span> {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-sm font-black uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm shadow-lg shadow-primary/20 transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Request...
          </> : <>
            Request Quote
            <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}